import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { StatusSeverity, StatusConfig, getStatusSeverity } from '@core/enums/model-status';

@Component({
	selector: 'app-status-cell-renderer',
	template: `
		@if (status) {
			<app-tag [value]="label | translate" [severity]="severity"></app-tag>
		}
	`,
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class StatusCellRendererComponent implements ICellRendererAngularComp, OnChanges {
	/**
	 * Only consumed when the component is used directly in a template
	 * (`<app-status-cell-renderer [value]="x" [config]="y">`), so a caller
	 * can resolve the label through a real translation key instead of the
	 * generic `defaultLabels` fallback below. Ag-grid's own cell-renderer
	 * usage never reads these `@Input()`s — it calls `agInit(params)`
	 * directly with its own `params.value`/`params.config`.
	 */
	@Input() value?: string | null;
	@Input() config?: Record<string, StatusConfig>;

	status: string | null = null;
	label: string | null = null;
	severity: StatusSeverity = 'secondary';

	ngOnChanges(changes: SimpleChanges): void {
		if ((changes['value'] || changes['config']) && this.value !== undefined) {
			this.agInit({ value: this.value, config: this.config });
		}
	}

	static getSeverity(statusValue: string, configSource?: Record<string, StatusConfig>): StatusSeverity {
		return configSource?.[statusValue]?.severity ?? getStatusSeverity(statusValue);
	}

	static getLabel(statusValue: string, configSource: Record<string, StatusConfig>): string {
		return configSource[statusValue]?.label || statusValue;
	}

	agInit(params: any): void {
		this.status = params.status || params.value || params.data?.status;
		const configSource: Record<string, StatusConfig> | undefined = params.config;
		const config = configSource?.[this.status!];

		const defaultLabels: Record<string, string> = {
			approved: 'common_status_confirmed_f',
			pending: 'common_status_pending',
			rejected: 'common_status_rejected_f',
			invalid: 'common_status_invalid',
			waived: 'common_status_waived_f',
		};

		this.label = params.label || config?.label || defaultLabels[this.status!] || this.status;
		this.severity = config?.severity || getStatusSeverity(this.status);
	}

	refresh(params: any): boolean {
		this.agInit(params);
		return true;
	}

	static valueGetter(params: any): string {
		return params.data?.status;
	}

	static filterValueGetter(
		params: any,
		translateService: any,
		configSource: Record<string, StatusConfig>,
		field: string = 'status'
	): string {
		const status = params.data?.[field];
		if (!status) return '';
		const labelKey = configSource[status]?.label || status;
		return translateService.instant(labelKey);
	}

	static comparator(valueA: any, valueB: any, translateService: any, configSource: Record<string, StatusConfig>): number {
		const labelKeyA = configSource[valueA]?.label || valueA || '';
		const labelKeyB = configSource[valueB]?.label || valueB || '';
		const labelA = translateService.instant(labelKeyA);
		const labelB = translateService.instant(labelKeyB);

		return labelA.localeCompare(labelB);
	}
}
